/**
 * Centralized logging utility with environment-based log levels
 * Reduces console chattiness by providing controlled logging levels
 */

/* eslint-disable no-console */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

class Logger {
  private currentLevel: LogLevel;
  private readonly context: string;

  constructor(context: string = 'App') {
    this.context = context;
    this.currentLevel = this.determineLogLevel();
  }

  private determineLogLevel(): LogLevel {
    const env = import.meta.env.MODE;
    
    // Check for explicit log level override
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL;
    if (envLogLevel) {
      switch (envLogLevel.toUpperCase()) {
        case 'DEBUG': return LogLevel.DEBUG;
        case 'INFO': return LogLevel.INFO;
        case 'WARN': return LogLevel.WARN;
        case 'ERROR': return LogLevel.ERROR;
        case 'NONE': return LogLevel.NONE;
      }
    }

    // Default log levels by environment
    switch (env) {
      case 'development':
        return LogLevel.DEBUG;
      case 'staging':
        return LogLevel.INFO;
      case 'production':
        return LogLevel.ERROR;
      default:
        return LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private formatMessage(level: string, message: string, ...args: unknown[]): [string, ...unknown[]] {
    const timestamp = new Date().toISOString().substring(11, 23);
    const prefix = `[${timestamp}] ${level} [${this.context}]`;
    return [prefix + ' ' + message, ...args];
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage('DEBUG', message, ...args));
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.formatMessage('INFO', message, ...args));
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', message, ...args));
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.formatMessage('ERROR', message, ...args));
    }
  }

  /**
   * Always logs regardless of level - use sparingly for critical info
   */
  force(message: string, ...args: unknown[]): void {
    console.log(...this.formatMessage('FORCE', message, ...args));
  }

  /**
   * Create a child logger with a specific context
   */
  child(childContext: string): Logger {
    return new Logger(`${this.context}:${childContext}`);
  }

  /**
   * Get current log level for debugging
   */
  getCurrentLevel(): LogLevel {
    return this.currentLevel;
  }

  /**
   * Temporarily override log level (useful for debugging specific components)
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }
}

// Export default logger instance
export const logger = new Logger('MediaHubOps');

// Export factory function for component-specific loggers
export const createLogger = (context: string): Logger => new Logger(context);

// Export convenience methods for quick logging
export const log = {
  debug: (message: string, ...args: unknown[]) => logger.debug(message, ...args),
  info: (message: string, ...args: unknown[]) => logger.info(message, ...args),
  warn: (message: string, ...args: unknown[]) => logger.warn(message, ...args),
  error: (message: string, ...args: unknown[]) => logger.error(message, ...args),
  force: (message: string, ...args: unknown[]) => logger.force(message, ...args),
};
